import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { ProductSubscriptionRenewPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import { organizationSelector } from '../../../store/redux-slices/organizations/selectors';
import {
  ORGANIZATION_LOADING_PROCESSING,
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  SUBSCRIPTION_EDITING_LOADING_PROCESSING,
} from '../../../store/redux-slices/processes/constants';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import {
  clearTLCurrentSubscriptionEditing,
  clearTLSubscriptionHelperData,
} from '../../../store/redux-slices/subscriptions/travelLicense';
import {
  editingTLSubscriptionDataSelector,
  tlSubscriptionRenewalSuggestionSelector,
} from '../../../store/redux-slices/subscriptions/travelLicense/selectors';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';
import {
  loadDatesForNewTLSubscription,
  loadEditingTLSubscription,
  loadTLSubscriptionRenewalSuggestion,
} from '../../../store/saga-slices/subscriptions/travelLicense';
import {
  ILoadEditingTLSubscriptionPayload,
  ILoadTLSubscriptionRenewalSuggestionPayload,
} from '../../../store/saga-slices/subscriptions/travelLicense/models';

export const useTLEditSubscriptionRenewPage = () => {
  const { organizationId, subscriptionId, productType } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const LoadOrganization = useActions(loadOrganization);
  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewTLSubscription);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadEditingSubscription = useActions(loadEditingTLSubscription);

  const ClearTLSubscriptionHelperData = useActions(
    clearTLSubscriptionHelperData,
  );
  const subscriptionData = useSelector(editingTLSubscriptionDataSelector);

  const ClearCurrentSubscriptionEditing = useActions(
    clearTLCurrentSubscriptionEditing,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);
  const LoadSubscriptionRenewalSuggestion = useActions(
    loadTLSubscriptionRenewalSuggestion,
  );

  const subscriptionRenewalSuggestion = useSelector(
    tlSubscriptionRenewalSuggestionSelector,
  );

  const isOrganizationDataLoading = useProcessing(
    ORGANIZATION_LOADING_PROCESSING,
  );

  const isEditingDataLoading = useProcessing(
    SUBSCRIPTION_EDITING_LOADING_PROCESSING,
  );

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const organization = useSelector(organizationSelector);

  const loadSubscriptionRenewalSuggestionHandler = useCallback(() => {
    const payload: ILoadTLSubscriptionRenewalSuggestionPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionRenewalSuggestion(payload);
  }, [LoadSubscriptionRenewalSuggestion, organizationId, subscriptionId]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    const payload: ILoadEditingTLSubscriptionPayload = {
      organizationId,
      subscriptionId,
      subscriptionType: productType,
    };

    LoadEditingSubscription(payload);
  }, [LoadEditingSubscription, organizationId, productType, subscriptionId]);

  useEffect(() => {
    LoadOrganizationContracts({ organizationId });
    LoadOrganization(organizationId);
    loadSubscriptionRenewalSuggestionHandler();
  }, [
    LoadOrganization,
    LoadOrganizationContracts,
    organizationId,
    subscriptionId,

    loadSubscriptionRenewalSuggestionHandler,
  ]);

  useEffect(
    () => () => {
      ClearTLSubscriptionHelperData();
      ClearOrganizationContracts();
      ClearSubscriptionHelperData();
      ClearCurrentSubscriptionEditing();
    },
    [
      ClearOrganizationContracts,
      ClearTLSubscriptionHelperData,
      ClearSubscriptionHelperData,
      ClearCurrentSubscriptionEditing,
    ],
  );

  const dataLoading =
    !organization ||
    isOrganizationDataLoading ||
    !subscriptionData ||
    (!subscriptionData && isEditingDataLoading) ||
    !subscriptionRenewalSuggestion ||
    (!subscriptionRenewalSuggestion && isSuggestionDataLoading);

  return {
    dataLoading,
    organization,
    loadDatesForNewSubscriptionHandler,
    loadSubscriptionRenewalSuggestionHandler,
  };
};
